import React, { useEffect, useRef, useState } from 'react';

/**
 * Reveal — wraps children in an intersection-observed container that
 * animates into view from a direction on first entry. Once revealed,
 * the animation is retained (no flicker on re-scroll).
 *
 * Props:
 *   - direction: 'left' | 'right' | 'up' | 'down' (default 'up')
 *   - delay:     milliseconds to stagger the reveal
 *   - once:      boolean (default true) — animate only the first time
 */
export const Reveal = ({ children, direction = 'up', delay = 0, once = true, className = '', ...rest }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const prefersReduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) {
      setVisible(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            if (once) io.disconnect();
          } else if (!once) {
            setVisible(false);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -60px 0px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [once]);

  return (
    <div
      ref={ref}
      className={`k9-reveal k9-reveal--${direction} ${visible ? 'k9-reveal--in' : ''} ${className}`}
      style={{ transitionDelay: visible ? `${delay}ms` : undefined }}
      {...rest}
    >
      {children}
    </div>
  );
};

export default Reveal;
